import { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Typography, Box, Paper, Stack,
    IconButton, Tooltip, Divider, Chip, InputAdornment,
    FormControlLabel, Checkbox
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FolderIcon from '@mui/icons-material/Folder';
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import { AlertInfo } from "../../components/Alerts/Alert";
import { api } from "../../services/api";
import { KanbanModulo } from "./TabKanbanModulos";

export type KanbanTarefa = {
    id_kanban_tarefa: number;
    id_kanban_grupo: number;
    id_kanban_modulo: number;
    id_escritorio_kanban: number;
    nome_tarefa: string;
    tempo_execucao_horas: number;
    tempo_definido_usuario: number;
    ordem_tarefa: number;
    kanban_tarefa_ativa: number;
};

export type KanbanGrupo = {
    id_kanban_grupo: number;
    id_escritorio_kanban: number;
    nome_grupo: string;
    kanban_grupo_ativo: number;
    tarefas: KanbanTarefa[];
};

type TarefaLocal = {
    tempId: string;
    id_kanban_modulo: number;
    nome_tarefa: string;
    tempo_execucao_horas: number;
    tempo_definido_usuario: boolean;
};

type Props = {
    open: boolean;
    onClose: () => void;
    onSaved: () => void;
    modulos: KanbanModulo[];
    grupo: KanbanGrupo | null;
};

export default function ModalKanbanTarefa({ open, onClose, onSaved, modulos, grupo }: Props) {
    const [openLoad, setOpenLoad] = useState(false);
    const [nomeGrupo, setNomeGrupo] = useState('');
    const [tarefasLocal, setTarefasLocal] = useState<TarefaLocal[]>([]);
    const [novoNomes, setNovoNomes] = useState<Record<number, string>>({});
    const [novoHoras, setNovoHoras] = useState<Record<number, string>>({});
    const [novoTempoUsuario, setNovoTempoUsuario] = useState<Record<number, boolean>>({});
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editNome, setEditNome] = useState('');
    const [editHoras, setEditHoras] = useState('');
    const [editTempoUsuario, setEditTempoUsuario] = useState(false);

    const isEdicao = grupo !== null;

    useEffect(() => {
        if (open) {
            setNovoNomes({});
            setNovoHoras({});
            setNovoTempoUsuario({});
            setEditingId(null);
            if (grupo) {
                setNomeGrupo(grupo.nome_grupo);
                setTarefasLocal(
                    grupo.tarefas.map(t => ({
                        tempId: `existing_${t.id_kanban_tarefa}`,
                        id_kanban_modulo: t.id_kanban_modulo,
                        nome_tarefa: t.nome_tarefa,
                        tempo_execucao_horas: t.tempo_execucao_horas,
                        tempo_definido_usuario: !!t.tempo_definido_usuario,
                    }))
                );
            } else {
                setNomeGrupo('');
                setTarefasLocal([]);
            }
        }
    }, [open, grupo]);

    function handleAdicionarLocal(id_kanban_modulo: number) {
        const nome = novoNomes[id_kanban_modulo]?.trim();
        if (!nome) return AlertInfo({ icon: 'info', title: 'Informe o nome da tarefa' });

        const tempoUsuario = novoTempoUsuario[id_kanban_modulo] || false;
        const horas = tempoUsuario ? 0 : (parseFloat(novoHoras[id_kanban_modulo] || '0') || 0);

        setTarefasLocal(prev => [
            ...prev,
            {
                tempId: `${Date.now()}_${Math.random()}`,
                id_kanban_modulo,
                nome_tarefa: nome,
                tempo_execucao_horas: horas,
                tempo_definido_usuario: tempoUsuario,
            },
        ]);
        setNovoNomes(prev => ({ ...prev, [id_kanban_modulo]: '' }));
        setNovoHoras(prev => ({ ...prev, [id_kanban_modulo]: '' }));
        setNovoTempoUsuario(prev => ({ ...prev, [id_kanban_modulo]: false }));
    }

    function handleRemoverLocal(tempId: string) {
        setTarefasLocal(prev => prev.filter(t => t.tempId !== tempId));
    }

    function handleEditarLocal(tarefa: TarefaLocal) {
        setEditingId(tarefa.tempId);
        setEditNome(tarefa.nome_tarefa);
        setEditHoras(tarefa.tempo_definido_usuario ? '' : String(tarefa.tempo_execucao_horas || ''));
        setEditTempoUsuario(tarefa.tempo_definido_usuario);
    }

    function handleSalvarEdicao() {
        if (!editingId) return;
        if (!editNome.trim()) return AlertInfo({ icon: 'info', title: 'Informe o nome da tarefa' });
        const horas = editTempoUsuario ? 0 : (parseFloat(editHoras) || 0);
        setTarefasLocal(prev => prev.map(t =>
            t.tempId === editingId
                ? { ...t, nome_tarefa: editNome.trim(), tempo_execucao_horas: horas, tempo_definido_usuario: editTempoUsuario }
                : t
        ));
        setEditingId(null);
    }

    function handleCancelarEdicao() {
        setEditingId(null);
    }

    function getTarefasLocalDoModulo(id_kanban_modulo: number) {
        return tarefasLocal.filter(t => t.id_kanban_modulo === id_kanban_modulo);
    }

    async function handleSalvar() {
        if (!nomeGrupo.trim())
            return AlertInfo({ icon: 'info', title: 'Informe o nome do grupo de tarefas' });

        if (tarefasLocal.length === 0)
            return AlertInfo({ icon: 'info', title: 'Adicione pelo menos uma tarefa' });

        setOpenLoad(true);
        try {
            const body = {
                nome_grupo: nomeGrupo.trim(),
                tarefas: tarefasLocal.map(t => ({
                    id_kanban_modulo: t.id_kanban_modulo,
                    nome_tarefa: t.nome_tarefa,
                    tempo_execucao_horas: t.tempo_execucao_horas,
                    tempo_definido_usuario: t.tempo_definido_usuario,
                })),
            };

            let res;
            if (isEdicao) {
                res = await api.put(`/updatekanbangrupo/${grupo!.id_kanban_grupo}`, body);
            } else {
                res = await api.post('/createkanbangrupo', body);
            }

            if (res.data.success) {
                AlertInfo({ icon: 'success', title: isEdicao ? 'Grupo atualizado!' : 'Grupo criado com sucesso!' });
                onSaved();
                onClose();
            } else {
                AlertInfo({ icon: 'error', title: res.data.message || 'Falha na operação' });
            }
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <SimpleBackdrop open={openLoad} />

            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isEdicao ? <EditIcon color="primary" /> : <FolderIcon color="primary" />}
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            {isEdicao ? 'Editar Grupo de Tarefas' : 'Novo Grupo de Tarefas'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isEdicao ? 'Altere o nome e as tarefas do grupo' : 'Nomeie o grupo e adicione tarefas em cada módulo'}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                {/* Nome do grupo */}
                <TextField
                    label="Nome do Grupo de Tarefas *"
                    fullWidth
                    size="small"
                    value={nomeGrupo}
                    onChange={e => setNomeGrupo(e.target.value)}
                    inputProps={{ maxLength: 255 }}
                    sx={{ mb: 2.5, mt: 0.5 }}
                />

                {/* Módulos com inputs de tarefas */}
                {modulos.map(modulo => {
                    const moduloTarefas = getTarefasLocalDoModulo(modulo.id_kanban_modulo);
                    const novoNome = novoNomes[modulo.id_kanban_modulo] || '';
                    const novoHora = novoHoras[modulo.id_kanban_modulo] || '';

                    return (
                        <Paper
                            key={modulo.id_kanban_modulo}
                            variant="outlined"
                            sx={{ p: 2, mb: 2, borderRadius: 2 }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                                <ViewKanbanIcon color="primary" sx={{ fontSize: 20 }} />
                                <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
                                    {modulo.nome_modulo}
                                </Typography>
                                {moduloTarefas.length > 0 && (
                                    <Chip
                                        size="small"
                                        label={`${moduloTarefas.length} tarefa${moduloTarefas.length !== 1 ? 's' : ''}`}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontSize: 11, height: 22 }}
                                    />
                                )}
                            </Stack>

                            {/* Tarefas já adicionadas localmente */}
                            {moduloTarefas.length > 0 && (
                                <Stack spacing={0.5} mb={1.5}>
                                    {moduloTarefas.map((tarefa, idx) => (
                                        <Box key={tarefa.tempId}>
                                            {editingId === tarefa.tempId ? (
                                                <Box sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: '#e3f2fd' }}>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            value={editNome}
                                                            onChange={e => setEditNome(e.target.value)}
                                                            onKeyDown={e => { if (e.key === 'Enter') handleSalvarEdicao(); if (e.key === 'Escape') handleCancelarEdicao(); }}
                                                            autoFocus
                                                            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.8 } }}
                                                        />
                                                        {!editTempoUsuario && (
                                                            <TextField
                                                                size="small"
                                                                value={editHoras}
                                                                onChange={e => setEditHoras(e.target.value)}
                                                                onKeyDown={e => { if (e.key === 'Enter') handleSalvarEdicao(); if (e.key === 'Escape') handleCancelarEdicao(); }}
                                                                type="number"
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end"><Typography variant="caption">h</Typography></InputAdornment>,
                                                                }}
                                                                sx={{ width: 100, '& .MuiInputBase-input': { fontSize: 13, py: 0.8 } }}
                                                            />
                                                        )}
                                                        <Tooltip title="Confirmar">
                                                            <IconButton size="small" color="primary" onClick={handleSalvarEdicao}>
                                                                <CheckIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Cancelar">
                                                            <IconButton size="small" color="default" onClick={handleCancelarEdicao}>
                                                                <CloseIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={editTempoUsuario}
                                                                onChange={e => setEditTempoUsuario(e.target.checked)}
                                                            />
                                                        }
                                                        label={<Typography variant="caption" color="text.secondary">Tempo definido pelo usuário</Typography>}
                                                        sx={{ ml: 0, mt: 0 }}
                                                    />
                                                </Box>
                                            ) : (
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1}
                                                    sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: '#fafafa' }}
                                                >
                                                    <AssignmentIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                                    <Typography variant="body2" sx={{ flex: 1, fontSize: 13 }}>
                                                        {tarefa.nome_tarefa}
                                                    </Typography>
                                                    {tarefa.tempo_definido_usuario ? (
                                                        <Chip
                                                            size="small"
                                                            icon={<AccessTimeIcon sx={{ fontSize: 13 }} />}
                                                            label="Definido pelo usuário"
                                                            sx={{ fontSize: 11, height: 20, bgcolor: '#fff3e0', color: '#e65100' }}
                                                        />
                                                    ) : tarefa.tempo_execucao_horas > 0 ? (
                                                        <Chip
                                                            size="small"
                                                            icon={<AccessTimeIcon sx={{ fontSize: 13 }} />}
                                                            label={`${tarefa.tempo_execucao_horas}h`}
                                                            sx={{ fontSize: 11, height: 20, bgcolor: '#e3f2fd', color: '#1565c0' }}
                                                        />
                                                    ) : null}
                                                    <Tooltip title="Editar">
                                                        <IconButton size="small" color="primary" onClick={() => handleEditarLocal(tarefa)}>
                                                            <EditIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Remover">
                                                        <IconButton size="small" color="error" onClick={() => handleRemoverLocal(tarefa.tempId)}>
                                                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            )}
                                            {idx < moduloTarefas.length - 1 && <Divider sx={{ mt: 0.5 }} />}
                                        </Box>
                                    ))}
                                </Stack>
                            )}

                            {/* Input para adicionar nova tarefa */}
                            <Stack spacing={0.5}>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Nome da tarefa..."
                                        value={novoNome}
                                        onChange={e => setNovoNomes(prev => ({ ...prev, [modulo.id_kanban_modulo]: e.target.value }))}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') handleAdicionarLocal(modulo.id_kanban_modulo);
                                        }}
                                        sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.8 } }}
                                    />
                                    {!(novoTempoUsuario[modulo.id_kanban_modulo]) && (
                                        <TextField
                                            size="small"
                                            placeholder="0"
                                            value={novoHora}
                                            onChange={e => setNovoHoras(prev => ({ ...prev, [modulo.id_kanban_modulo]: e.target.value }))}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') handleAdicionarLocal(modulo.id_kanban_modulo);
                                            }}
                                            type="number"
                                            InputProps={{
                                                endAdornment: <InputAdornment position="end"><Typography variant="caption">h</Typography></InputAdornment>,
                                            }}
                                            sx={{ width: 100, '& .MuiInputBase-input': { fontSize: 13, py: 0.8 } }}
                                        />
                                    )}
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        disableElevation
                                        onClick={() => handleAdicionarLocal(modulo.id_kanban_modulo)}
                                        sx={{ minWidth: 0, px: 1.5, textTransform: 'none', fontSize: 12 }}
                                        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                    >
                                        Adicionar
                                    </Button>
                                </Stack>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={novoTempoUsuario[modulo.id_kanban_modulo] || false}
                                            onChange={e => setNovoTempoUsuario(prev => ({ ...prev, [modulo.id_kanban_modulo]: e.target.checked }))}
                                        />
                                    }
                                    label={
                                        <Typography variant="caption" color="text.secondary">
                                            Tempo de execução definido pelo usuário
                                        </Typography>
                                    }
                                    sx={{ ml: 0, mt: -0.5 }}
                                />
                            </Stack>
                        </Paper>
                    );
                })}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} color="inherit" variant="outlined">
                    Cancelar
                </Button>
                <Button onClick={handleSalvar} variant="contained" disableElevation>
                    {isEdicao ? 'Salvar Alterações' : 'Criar Grupo'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
